import { useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { CapaianPrestasi } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: CapaianPrestasi) => {
  const response = await api.post("/master/capaian-prestasi/store", newData)
  return response.data
}

const patchData = async (newData: CapaianPrestasi) => {
  const response = await api.patch("/master/capaian-prestasi/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_capaian_prestasi: number }) => {
  const response = await api({
    url: "/master/capaian-prestasi/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: CapaianPrestasi | null
  closeModal: () => void
}) {
  const [form, setForm] = useState({
    id_ref_capaian_prestasi: selectedRow?.id_ref_capaian_prestasi || 0,
    nama_capaian_prestasi: selectedRow?.nama_capaian_prestasi || "",
    persentase_capaian_prestasi: selectedRow?.persentase_capaian_prestasi || 0,
    is_aktif: selectedRow?.is_aktif ?? true,
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listCapaianPrestasi"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Capaian Prestasi"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_capaian_prestasi}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={
          isPost
            ? "Insert Data Capaian Prestasi"
            : "Update Data Capaian Prestasi"
        }
      />
      <div>
        <Label>Nama Capaian Pestasi</Label>
        <Input
          required
          placeholder="Ketik nama capaian prestasi..."
          value={form.nama_capaian_prestasi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              nama_capaian_prestasi: event.target.value,
            }))
          }
        />
      </div>
      <div>
        <Label>Persentase</Label>
        <Input
          required
          type="number"
          placeholder="Ketik persentase capaian prestasi..."
          value={form.persentase_capaian_prestasi}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              persentase_capaian_prestasi: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="flex gap-3">
        <Switch
          id="status"
          checked={form.is_aktif}
          onCheckedChange={() =>
            setForm((prev) => ({
              ...prev,
              is_aktif: !prev.is_aktif,
            }))
          }
        />
        <Label htmlFor="status">Status Nonaktif/Aktif</Label>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
